import SEO from "@/components/feature/SEO";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { categories } from "@/mocks/categories";
import { videos } from "@/mocks/videos";
import type { Video } from "@/mocks/types";

function VideoCard({ video }: { video: Video }) {
  return (
    <Link
      to={`/article/${video.id}`}
      className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300/60 transition-all duration-300"
    >
      <div className="relative w-full aspect-video overflow-hidden">
        <img
          src={video.thumbnail}
          alt={video.title}
          title={`${video.title} - 红桃视频在线观看`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute bottom-2 right-2 bg-black/70 backdrop-blur-sm text-white text-xs px-2 py-0.5 rounded-md">
          {video.duration}
        </div>
        <div className="absolute top-2 left-2">
          <span className="bg-primary-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">
            {video.rating}
          </span>
        </div>
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="w-14 h-14 flex items-center justify-center bg-primary-500/90 rounded-full">
            <i className="ri-play-fill text-white text-2xl ml-0.5"></i>
          </div>
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-sm font-semibold text-foreground-950 line-clamp-1 mb-1.5 group-hover:text-primary-500 transition-colors">
          {video.title}
        </h3>
        <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed">
          {video.description}
        </p>
        <div className="flex items-center gap-2 mt-3">
          {video.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="text-xs text-secondary-600 bg-secondary-100/70 px-2 py-0.5 rounded-full">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const currentCat = categories.find((c) => c.slug === slug) || categories[0];
  const filteredVideos = slug
    ? videos.filter((v) => v.category === slug)
    : videos;

  return (
    <>
      <SEO
        title={`${currentCat.name} - 视频分类 | 红桃视频网页版`}
        description={`红桃视频网页版${currentCat.name}分类，为您提供海量高清${currentCat.name}在线免费观看。丰富的${currentCat.name}资源，高清画质，流畅播放。`}
        keywords={`红桃视频网页版,红桃视频在线观看,${currentCat.name},红桃视频${currentCat.name}`}
        canonical={`https://www.hongtao-video.com/category/${slug}`}
      />

      <ScrollToTop />

      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-16">
          <div className="bg-background-100">
            <Breadcrumb
              items={[
                { label: "首页", path: "/" },
                { label: "分类" },
                { label: currentCat.name },
              ]}
            />
          </div>

          <section className="w-full px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-7xl mx-auto">
              <div className="mb-8">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                  {currentCat.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-2">
                  共找到 {filteredVideos.length} 部{currentCat.name}作品
                </p>
              </div>

              <div className="flex gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                      currentCat.slug === cat.slug
                        ? "bg-primary-500 text-white"
                        : "bg-background-100 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    <i className={`${cat.icon} text-sm`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>

              {filteredVideos.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-product-shop="">
                  {filteredVideos.map((video) => (
                    <VideoCard key={video.id} video={video} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                    <i className="ri-vidicon-line text-3xl"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无内容，请查看其他分类</p>
                </div>
              )}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}